import React, { useState, useEffect } from 'react';
import { authFetch } from '../lib/authFetch';
import { t, getMonthShort } from '../lib/i18n';
import Icon from './Icon';
import { scoreTextColor, scoreHexColor, pickScore } from '../lib/scoring';

/**
 * FirmTimeline — shows score history for a single firm as an SVG chart.
 *
 * Pure component. Fetches its own data. Safe to add/remove.
 *
 * Props:
 *   identityId  — firm identifier (to find in historical runs)
 *   companyName — firm name (fallback matching)
 *   currentScore — current audit score (shown as last point)
 */
const _historyCache = {};

export default function FirmTimeline({ identityId, companyName, currentScore }) {
    const [history, setHistory] = useState(null); // [{period, score, date}]
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!identityId) return;
        if (_historyCache[identityId]) { setHistory(_historyCache[identityId]); return; }
        setLoading(true);
        authFetch('/api/proxy/audit?limit=20&offset=0')
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (!data || !data.runs) { setHistory([]); return; }
                const points = [];
                const MSHORT = getMonthShort();
                for (const run of data.runs) {
                    const results = run.results || [];
                    const match = results.find(r =>
                        r.identityId === identityId ||
                        (companyName && (r.companyName || '').toLowerCase() === companyName.toLowerCase())
                    );
                    if (match && typeof match.score === 'number') {
                        const period = run.period
                            ? `${MSHORT[(run.period.monthTo || 1) - 1]} ${run.period.year}`
                            : new Date(run.savedAt).toLocaleDateString('no-NO', { month: 'short', year: 'numeric' });
                        points.push({
                            period,
                            score: match.score,
                            date: run.savedAt,
                            errCount: match.errCount || 0,
                        });
                    }
                }
                // Sort by date ascending
                points.sort((a, b) => new Date(a.date) - new Date(b.date));
                // Deduplicate by period (keep latest)
                const seen = new Map();
                points.forEach(p => seen.set(p.period, p));
                const result = [...seen.values()];
                _historyCache[identityId] = result;
                setHistory(result);
            })
            .catch(() => setHistory([]))
            .finally(() => setLoading(false));
    }, [identityId]);

    if (loading) {
        return (
            <div className="flex items-center gap-2 py-3 text-xs text-slate-400">
                <div className="w-3 h-3 border-2 border-copper-500 border-t-transparent rounded-full animate-spin" />
                {t('timeline_loading')}
            </div>
        );
    }

    if (!history || history.length === 0) {
        return (
            <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Icon name="history" className="w-3 h-3" />
                    {t('timeline_first_audit')}
                </div>
            </div>
        );
    }

    if (history.length === 1) {
        const p = history[0];
        const scoreCol = scoreTextColor(pickScore(p));
        return (
            <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="flex items-center justify-between">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                        <Icon name="history" className="w-3 h-3" />
                        {t('timeline_1_period')}
                    </span>
                    <span className={`text-sm font-bold ${scoreCol}`}>{pickScore(p).toFixed(1)}</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-1">{p.period} · {p.errCount} {t('hero_errors')} — {t('timeline_trend_next')}</div>
            </div>
        );
    }

    // Chart dimensions
    const W = 320, H = 80, PAD = 24;
    const chartW = W - PAD * 2;
    const chartH = H - PAD;
    const minScore = 0, maxScore = 10;

    const points = history.map((p, i) => ({
        x: PAD + (i / (history.length - 1)) * chartW,
        y: PAD / 2 + chartH - ((p.score - minScore) / (maxScore - minScore)) * chartH,
        ...p,
    }));

    const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    // Trend: compare first and last
    const first = history[0].score;
    const last = history[history.length - 1].score;
    const delta = last - first;
    const trendColor = delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-600' : 'text-slate-400';
    const trendIcon = delta > 0 ? 'trending-up' : delta < 0 ? 'trending-down' : 'trending-up';

    return (
        <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                    <Icon name="history" className="w-3 h-3" />
                    {t('timeline_n_periods', history.length)}
                </span>
                <span className={`text-xs font-bold flex items-center gap-1 ${trendColor}`}>
                    <Icon name={trendIcon} className="w-3 h-3" />
                    {delta > 0 ? '+' : ''}{delta.toFixed(1)}
                </span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxWidth: '320px', height: '80px' }}>
                {/* Grid lines */}
                {[0, 2.5, 5, 7.5, 10].map(v => {
                    const y = PAD / 2 + chartH - (v / 10) * chartH;
                    return <line key={v} x1={PAD} y1={y} x2={W - PAD} y2={y} stroke="#e2e8f0" strokeWidth="0.5" />;
                })}
                {/* Line */}
                <path d={pathD} fill="none" stroke="#AA5D26" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                {/* Area fill */}
                <path d={`${pathD} L ${points[points.length - 1].x} ${PAD / 2 + chartH} L ${points[0].x} ${PAD / 2 + chartH} Z`}
                    fill={`url(#timelineGrad-${identityId})`} opacity="0.15" />
                <defs>
                    <linearGradient id={`timelineGrad-${identityId}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#AA5D26" />
                        <stop offset="100%" stopColor="#AA5D26" stopOpacity="0" />
                    </linearGradient>
                </defs>
                {/* Points */}
                {points.map((p, i) => (
                    <g key={i}>
                        <circle cx={p.x} cy={p.y} r="3.5"
                            fill={scoreHexColor(pickScore(p))}
                            stroke="white" strokeWidth="1.5" />
                        {/* Period label (show first, last, and middle if >4 points) */}
                        {(i === 0 || i === points.length - 1 || (points.length > 4 && i === Math.floor(points.length / 2))) && (
                            <text x={p.x} y={H - 2} textAnchor="middle" className="text-[8px]" fill="#94a3b8">
                                {p.period}
                            </text>
                        )}
                        {/* Score label on each point */}
                        <text x={p.x} y={p.y - 6} textAnchor="middle" className="text-[9px] font-bold" fill={scoreHexColor(pickScore(p))}>
                            {pickScore(p).toFixed(1)}
                        </text>
                    </g>
                ))}
            </svg>
        </div>
    );
}
